'use client'

import { Fragment } from 'react'
import { createPortal } from 'react-dom'
import { Transition } from '@headlessui/react'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { XCircleIcon } from '@heroicons/react/24/outline'
import { XMarkIcon } from '@heroicons/react/20/solid'
import { useNotification } from '@/utils/notifications'

import clsx from 'clsx'
import { useMounted } from '@/hooks/useMounted'

const variants = {
  warning: {
    classes: {
      root: 'border-amber-400',
    },
    icon: (
      <ExclamationTriangleIcon
        className="h-6 w-6 text-amber-500"
        aria-hidden="true"
      />
    ),
  },
  error: {
    classes: {
      root: 'border-red-700',
    },
    icon: <XCircleIcon className="h-6 w-6 text-red-700" aria-hidden="true" />,
  },
  default: {
    classes: {
      root: 'border-emerald-700',
    },
    icon: (
      <CheckCircleIcon
        className="h-6 w-6 text-emerald-700"
        aria-hidden="true"
      />
    ),
  },
}
type NotificationVariant = keyof typeof variants

const getVariantConfig = (variant: NotificationVariant) =>
  variants[variant] ? variants[variant] : variants.default

export default function Notification() {
  const { show, title, body, isError, variant, hideNotification } =
    useNotification()

  const mounted = useMounted()

  const { classes, icon } = getVariantConfig(
    isError ? 'error' : (variant as NotificationVariant) || 'default'
  )

  if (!mounted) return null

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    hideNotification()
  }

  return createPortal(
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-x-0 inset-y-0 z-50 flex items-end px-4 py-6 sm:items-start sm:p-6"
    >
      <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
        <Transition
          show={show}
          as={Fragment}
          enter="transform ease-in-out duration-300 transition"
          enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
          enterTo="translate-y-0 opacity-100 sm:translate-x-0"
          leave="transform ease-in-out duration-300 transition"
          leaveFrom="translate-y-0 opacity-100 sm:translate-x-0"
          leaveTo="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        >
          <div
            className={clsx(
              `pointer-events-auto max-w-2xl overflow-hidden rounded-md border-l-8 bg-white shadow-lg ring-1 ring-black ring-opacity-5`,
              classes.root
            )}
          >
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">{icon}</div>
                <div className="ml-3 flex-1 overflow-hidden break-words pt-0.5">
                  {title && (
                    <p className="text-sm font-medium text-gray-900">{title}</p>
                  )}
                  {body && (
                    <p
                      className={clsx(
                        'text-sm text-gray-500',
                        title ? 'mt-1' : ''
                      )}
                    >
                      {body}
                    </p>
                  )}
                </div>
                <div className="ml-4 flex flex-shrink-0">
                  <button
                    type="button"
                    className="inline-flex cursor-pointer rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>,
    document.body
  )
}
